import { useState, useCallback, useEffect } from 'react';
import { XIcon } from '../components/Icons';
import type { FilterableField } from '../types/index';
import { CadastroTemplate, DetailField, type TableColumn } from '../components/CadastroTemplate';
import { http } from '../auth/http';

interface BeneficiarioType {
  ideRegistro: number;
  ideBeneficiario: number;
  seqBeneficiario: number;
  ideBeneficiarioMae: number | null;
  codCPF: string;
  nmeCompleto: string;
  nmeSocial: string | null;
  nmeMaeCompleto: string;
  nmePaiCompleto: string | null;
  idePlano: number | null;
  desPlano: string | null; 
  staBeneficiario: 'ativo' | 'inativo';
  dtaAdesao: string | null;
  dtaRecadastro: string | null;
  dtaCancelamento: string | null;
  dtaNascimento: string | null;
  desMotivoCancelamento: string | null;
  desSituacao: string | null;
  tpoCusteio: string | null;
  tpoVinculo: string | null;
  staOpcional: number | null;
  codSiape: string | null;
  ideUF: number | null;
  sglUf: string | null;
  codCEP: string | null;
  ideConveniada: number | null;
  desConveniada: string | null;
  staSuspensao: number | null;
  staReciprocidade: number | null;
  codCNS: string | null;
  desEstadoCivil: string | null;
  desRaca: string | null;
  sglSexo: 'M' | 'F';
  codMatricula: number | null;
  desEmail: string | null;
  desEmail2: string | null;
  dtaAtualizacaoEmail: string | null;
  dtaAtualizacaoDadosBancarios: string | null;
  dtaAtualizacaoEndereco: string | null;
  dtaAtualizacaoContato: string | null;
  dtaAdocao: string | null;
  staEnviaEmailTitular: number | null;
  staCumpreCpt: number | null;
  staReingresso: number | null;
  staPPE: number | null;
  staVinculadoFinanceiro: number | null;
}


const filterableFields: FilterableField[] = [
    { value: 'nmeCompleto', label: 'Nome' },
    { value: 'codCPF', label: 'CPF' },
    { value: 'nmeSocial', label: 'Nome Social' },
    { value: 'codSiape', label: 'SIAPE' },
    { value: 'desPlano', label: 'Plano' },
    { value: 'desSituacao', label: 'Situação' },
    { value: 'sglUf', label: 'UF' },
    { value: 'desConveniada', label: 'Conveniada' },
    { value: 'tpoVinculo', label: 'Vínculo' },
    { value: 'tpoCusteio', label: 'Custeio' },
    { value: 'staOpcional', label: 'Dep. Opcional' },
    { value: 'desMotivoCancelamento', label: 'Motivo do Cancelamento' },
    { value: 'seqBeneficiario', label: 'Sequencial' },
    { value: 'ideBeneficiarioMae', label: 'ID Titular (Agregado)' },
    { value: 'codCEP', label: 'CEP' },
];

type BeneficiarioComId = BeneficiarioType & { id: number };

const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'N/A';
    return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(dateObj);
};

const formatInputDate = (date: string | null) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
}

const BeneficiarioModal = ({ data: beneficiario, onClose, isEditing, onSave }: { data: BeneficiarioComId | null; onClose: () => void; isEditing: boolean; onSave: (data: BeneficiarioComId) => void; }) => {
    const [activeTab, setActiveTab] = useState('pessoal');
    const [formData, setFormData] = useState<BeneficiarioComId | null>(beneficiario);

    useEffect(() => {
        setFormData(beneficiario);
    }, [beneficiario]);

    if (!formData) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        let processedValue: string | number | null = value;
    
        if (type === 'date') {
            processedValue = value ? new Date(value).toISOString() : null;
        } else if (name.startsWith('sta')) {
             processedValue = value === '' ? null : Number(value);
        } else if (type === 'number' || ['idePlano', 'codMatricula'].includes(name)) {
            processedValue = value === '' ? null : parseInt(value, 10);
        }
        
        setFormData(prev => prev ? { ...prev, [name]: processedValue } : null);
    };

    const handleSave = () => {
        if (formData) onSave(formData);
    };
    
    const booleanOptions = [{value: '1', label: 'Sim'}, {value: '0', label: 'Não'}];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'pessoal': return (
                <div className="detail-grid">
                    <DetailField label="Nome Completo" value={formData.nmeCompleto} isEditing={isEditing} name="nmeCompleto" onChange={handleChange} />
                    <DetailField label="Nome Social" value={formData.nmeSocial} isEditing={isEditing} name="nmeSocial" onChange={handleChange} />
                    <DetailField label="Data de Nascimento" value={isEditing ? formatInputDate(formData.dtaNascimento) : formatDate(formData.dtaNascimento)} isEditing={isEditing} name="dtaNascimento" type="date" onChange={handleChange} />
                    <DetailField label="Nome da Mãe" value={formData.nmeMaeCompleto} isEditing={isEditing} name="nmeMaeCompleto" onChange={handleChange} />
                    <DetailField label="Nome do Pai" value={formData.nmePaiCompleto} isEditing={isEditing} name="nmePaiCompleto" onChange={handleChange} />
                    <DetailField label="Sexo" value={formData.sglSexo} isEditing={isEditing} name="sglSexo" type="select" options={[{value: 'M', label: 'Masculino'}, {value: 'F', label: 'Feminino'}]} onChange={handleChange} />
                    <DetailField label="Estado Civil" value={formData.desEstadoCivil} isEditing={isEditing} name="desEstadoCivil" onChange={handleChange} />
                    <DetailField label="Raça/Cor" value={formData.desRaca} isEditing={isEditing} name="desRaca" onChange={handleChange} />
                </div>);
            case 'vinculo': return (
                <div className="detail-grid">
                    <DetailField label="ID do Grupo Familiar" value={formData.ideBeneficiario} />
                    <DetailField label="Sequencial no Grupo" value={formData.seqBeneficiario} />
                    <DetailField label="Vínculo" value={formData.tpoVinculo} isEditing={isEditing} name="tpoVinculo" onChange={handleChange}/>
                    <DetailField label="ID Titular (Agregado)" value={formData.ideBeneficiarioMae} />
                    <DetailField label="Plano" value={formData.desPlano} isEditing={isEditing} name="desPlano" />
                    <DetailField label="Conveniada" value={formData.desConveniada} isEditing={isEditing} name="desConveniada" onChange={handleChange}/>
                    <DetailField label="Situação" value={formData.desSituacao} isEditing={isEditing} name="desSituacao" onChange={handleChange}/>
                    <DetailField label="Custeio" value={formData.tpoCusteio} isEditing={isEditing} name="tpoCusteio" onChange={handleChange}/>
                    <DetailField label="Data de Adesão" value={isEditing ? formatInputDate(formData.dtaAdesao) : formatDate(formData.dtaAdesao)} isEditing={isEditing} name="dtaAdesao" type="date" onChange={handleChange} />
                    <DetailField label="Último Recadastro" value={isEditing ? formatInputDate(formData.dtaRecadastro) : formatDate(formData.dtaRecadastro)} isEditing={isEditing} name="dtaRecadastro" type="date" onChange={handleChange}/>
                    <DetailField label="Data do Cancelamento" value={isEditing ? formatInputDate(formData.dtaCancelamento) : formatDate(formData.dtaCancelamento)} isEditing={isEditing} name="dtaCancelamento" type="date" onChange={handleChange} />
                    <DetailField label="Motivo do Cancelamento" value={formData.desMotivoCancelamento} isEditing={isEditing} name="desMotivoCancelamento" />
                </div>);
            case 'documentos': return (
                <div className="detail-grid">
                    <DetailField label="CPF" value={formData.codCPF} isEditing={isEditing} name="codCPF" onChange={handleChange} />
                    <DetailField label="Matrícula" value={formData.codMatricula} isEditing={isEditing} name="codMatricula" type="number" onChange={handleChange}/>
                    <DetailField label="SIAPE" value={formData.codSiape} isEditing={isEditing} name="codSiape" onChange={handleChange}/>
                    <DetailField label="CNS" value={formData.codCNS} isEditing={isEditing} name="codCNS" onChange={handleChange}/>
                </div>);
            case 'contato': return (
                <div className="detail-grid">
                    <DetailField label="Email Principal" value={formData.desEmail} isEditing={isEditing} name="desEmail" type="email" onChange={handleChange}/>
                    <DetailField label="Email Secundário" value={formData.desEmail2} isEditing={isEditing} name="desEmail2" type="email" onChange={handleChange}/>
                    <DetailField label="CEP" value={formData.codCEP} isEditing={isEditing} name="codCEP" onChange={handleChange}/>
                    <DetailField label="UF" value={formData.sglUf} isEditing={isEditing} name="sglUf" onChange={handleChange}/>
                </div>);
             case 'indicadores': return (
                <div className="detail-grid">
                    <DetailField label="Status do Beneficiário" value={formData.staBeneficiario} isEditing={isEditing} name="staBeneficiario" type="select" options={[{value: 'ativo', label: 'Ativo'}, {value: 'inativo', label: 'Inativo'}]} onChange={handleChange}/>
                    <DetailField label="Dependente Opcional" value={String(formData.staOpcional)} isEditing={isEditing} name="staOpcional" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Suspenso" value={String(formData.staSuspensao)} isEditing={isEditing} name="staSuspensao" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Reciprocidade" value={String(formData.staReciprocidade)} isEditing={isEditing} name="staReciprocidade" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Politicamente Exposta (PPE)" value={String(formData.staPPE)} isEditing={isEditing} name="staPPE" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Cumpre CPT" value={String(formData.staCumpreCpt)} isEditing={isEditing} name="staCumpreCpt" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Reingresso" value={String(formData.staReingresso)} isEditing={isEditing} name="staReingresso" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Agregado Vinculado" value={String(formData.staVinculadoFinanceiro)} isEditing={isEditing} name="staVinculadoFinanceiro" type="select" options={booleanOptions} onChange={handleChange} />
                    <DetailField label="Recebe Email do Titular" value={String(formData.staEnviaEmailTitular)} isEditing={isEditing} name="staEnviaEmailTitular" type="select" options={booleanOptions} onChange={handleChange} />
                </div>);
            default: return null;
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <header className="modal-header">
                    <h3 className="modal-title">{isEditing ? `Editando: ${beneficiario?.nmeCompleto}` : beneficiario?.nmeCompleto}</h3>
                    <button onClick={onClose} className="btn-icon" aria-label="Fechar modal"><XIcon /></button>
                </header>
                <div className="modal-body">
                    <nav className="modal-tabs">
                        <button className={`tab-button ${activeTab === 'pessoal' ? 'active' : ''}`} onClick={() => setActiveTab('pessoal')}>Pessoal</button>
                        <button className={`tab-button ${activeTab === 'vinculo' ? 'active' : ''}`} onClick={() => setActiveTab('vinculo')}>Vínculo</button>
                        <button className={`tab-button ${activeTab === 'documentos' ? 'active' : ''}`} onClick={() => setActiveTab('documentos')}>Documentos</button>
                        <button className={`tab-button ${activeTab === 'contato' ? 'active' : ''}`} onClick={() => setActiveTab('contato')}>Contato</button>
                        <button className={`tab-button ${activeTab === 'indicadores' ? 'active' : ''}`} onClick={() => setActiveTab('indicadores')}>Indicadores</button>
                    </nav>
                    <div className="tab-content">{renderTabContent()}</div>
                </div>
                 {isEditing && (
                    <footer className="modal-footer">
                        <div className="btn-group">
                            <button className="btn btn-danger" onClick={onClose}>Cancelar</button>
                            <button className="btn btn-primary" onClick={handleSave}>Salvar Alterações</button>
                        </div>
                    </footer>
                )}
            </div>
        </div>
    );
};


const tableColumns: TableColumn<BeneficiarioComId>[] = [
    { header: 'Nome', key: 'nmeCompleto', render: (item) => item.nmeCompleto },
    { header: 'CPF', key: 'codCPF', render: (item) => item.codCPF },
    { header: 'Vínculo', key: 'tpoVinculo', render: (item) => item.tpoVinculo },
    { header: 'Plano', key: 'desPlano', render: (item) => item.desPlano },
    { 
      header: 'Status', 
      key: 'staBeneficiario', 
      render: (item) => <span className={`status-badge ${item.staBeneficiario}`}>{item.staBeneficiario}</span>,
      exportValue: (item) => item.staBeneficiario,
    },
];

function Beneficiario() {
  
  const fetchData = useCallback(async (): Promise<BeneficiarioComId[]> => {
    const data = await http.get<BeneficiarioType[]>('/api/beneficiarios');
    return data.map(b => ({ ...b, id: b.ideRegistro }));
  }, []);

  const updateData = useCallback(async (data: BeneficiarioComId): Promise<boolean> => {
    try {
        await http.put('/api/beneficiarios', data);
        return true;
    } catch (error) {
        alert((error as Error).message || 'Falha ao atualizar beneficiário.');
        return false;
    }
  }, []);
  
  return (
    <CadastroTemplate<BeneficiarioComId>
        currentPage="Beneficiários"
        pageTitle="Gestão de Beneficiários"
        entityName="Beneficiário"
        tableName="beneficiario_CA001_BENEFICIARIO"
        filterableFields={filterableFields}
        tableColumns={tableColumns}
        fetchData={fetchData}
        updateData={updateData}
        ModalComponent={BeneficiarioModal}
    />
  );
}

export default Beneficiario;